// MUI Imports
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid2,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';

import { CityMunicipality } from '../../types';
import { ExitPollsLocation } from './ExitPollsLocation';
import { Navigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

interface ExitPollsData {
  districtTotal: number;
  municipalities: {
    [municipality_id: string]: number;
  };
  [key: string]: any; // Allow for additional fields
}

const ExitPollsDashboard: React.FC = () => {
  const province = "SURIGAO DEL NORTE";
  const district = "DISTRICT 1";
  const districtTabPath = `tabulationsV2/SURIGAO DEL NORTE_DISTRICT 1/regions/REGION XIII/provinces/${province}/districts/${district}`;
  const position = "DISTRICT 1 REPRESENTATIVE";
  const candidate = "Lucille Sering";

  const { user, loading, hasPermissions } = useAuth();
  const [exitPolls, setExitPolls] = useState<ExitPollsData | null>(null);
  const [municipalities, setMunicipalities] = useState<CityMunicipality[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExitPolls = async () => {
      try {
        const statsDocRef = doc(db, 'stats', 'exitPolls');
        const statsDocSnap = await getDoc(statsDocRef);

        if (statsDocSnap.exists()) {
          const data = statsDocSnap.data() as ExitPollsData;
          setExitPolls(data);
        } else {
          setError('No exit polls stats found');
        }
      } catch (err) {
        setError('Error fetching exit polls stats');
        console.error(err);
      }
    };

    const fetchMunicipalities = async () => {
      try {
        const municipalitiesRef = collection(db, 'cities_municipalities');
        const q = query(
          municipalitiesRef, 
          where('district', '==', district),
          where('province', '==', province),
          // orderBy('city_municipality', 'asc')
        );

        const querySnapshot = await getDocs(q);
        const fetchedMunicipalities = querySnapshot.docs.map(doc => ({
          id: doc.id,
          city_municipality: doc.data().city_municipality,
          district: doc.data().district,
          province: doc.data().province
        }));

        setMunicipalities(fetchedMunicipalities);
      } catch (err) {
        console.error('Error fetching municipalities:', err);
        setError('Error fetching municipalities');
      }
    };

    if (!loading && user) {
      fetchExitPolls();
      fetchMunicipalities();
    }
  }, [user, loading, district, province]);

  if (!loading) {
    if (!user || !hasPermissions) {
      return <Navigate to="/" />;
    }
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" component="h1" gutterBottom>
        Exit Polls
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <ExitPollsLocation 
              title="SURIGAO DEL NORTE DISTRICT 1"
              exitPollsValue={exitPolls?.districtTotal ?? 0}
              tabulationDocPath={districtTabPath}
              position={position}
              candidate={candidate}
            />
          </Grid2>
          {municipalities.map((municipality) => (
            <Grid2 size={{ xs: 12, md: 6 }} key={municipality.id}>
              <ExitPollsLocation 
                title={municipality.city_municipality}
                exitPollsValue={exitPolls?.municipalities[municipality.id] ?? 0}
                tabulationDocPath={`${districtTabPath}/cities/${municipality.city_municipality}`}
                position={position}
                candidate={candidate}
              />
            </Grid2>
          ))}
        </Grid2>
      )}
    </Container>
  );
};

export default ExitPollsDashboard;
